/**
 * @fileoverview Provides foundational networking utilities including API base
 * URL resolution, authentication token retrieval and a strongly typed
 * `apiRequest` wrapper that standardises request semantics across the codebase.
 */

import { auth } from "./firebase";
import type { RequestOptions } from "../types/api";

/**
 * Custom API error class that preserves error codes from the server.
 */
export class ApiError extends Error {
  code?: string;
  status?: number;

  constructor(message: string, code?: string, status?: number) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
  }
}

// API base URL - use environment variable or default to localhost for development
// For subdomain access, honor the env-provided base URL instead of forcing a port
/**
 * Resolves the base URL used for API requests, respecting environment
 * configuration and local development edge cases.
 */
const getApiBaseUrl = () => {
  const envUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:3000";

  // If using subdomain on localhost (e.g., test.localhost), do not override env
  // This allows setting VITE_API_BASE_URL=http://localhost:8080 (or another port)
  if (typeof window !== 'undefined' && window.location.hostname.includes('.localhost')) {
    return envUrl;
  }

  // If running locally but ENV points to a remote Cloud Run URL, prefer localhost for debugging
  if (
    typeof window !== 'undefined' &&
    window.location.hostname === 'localhost' &&
    /run\.app|cloudrun/.test(envUrl) &&
    import.meta.env.VITE_USE_LOCAL_API !== 'false'
  ) {
    return "http://localhost:3000";
  }

  return envUrl;
};

export const API_BASE_URL = getApiBaseUrl();

/**
 * Helper function to get the current user's ID token.
 */
export const getAuthToken = async (): Promise<string | null> => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    return null;
  }

  try {
    return await currentUser.getIdToken();
  } catch (error) {
    console.error("Error getting auth token:", error);
    return null;
  }
};

/**
 * Generic API request function with authentication.
 *
 * @template T Expected response payload type.
 * @param endpoint Relative API path (e.g. `/sites`).
 * @param method HTTP verb to execute.
 * @param data Optional request payload which will be serialised unless it is a
 *   FormData instance.
 * @param requiresAuth When true the user's Firebase ID token is attached as a
 *   bearer token. Defaults to `true`.
 * @param options Additional request configuration such as query parameters or
 *   custom headers.
 * @returns Parsed response body cast to type `T` when available.
 * @throws Error when authentication fails, the network request is rejected or
 *   the API returns a non-2xx status code.
 */
export const apiRequest = async <T>(
  endpoint: string,
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH" = "GET",
  data?: unknown,
  requiresAuth: boolean = true,
  options?: RequestOptions
): Promise<T> => {
  // Prepare headers
  const headers: Record<string, string> = {};
  
  // Check if data is FormData
  const isFormData = data instanceof FormData;
  
  // Only set Content-Type if not FormData (FormData sets its own boundary)
  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }
  headers["Accept"] = "application/json";
  headers["x-client-app"] = "landing-webapp";
  const clientVersion = import.meta.env.VITE_APP_VERSION || import.meta.env.MODE;
  if (clientVersion) {
    headers["x-client-version"] = clientVersion;
  }
  const clientBuild = import.meta.env.VITE_APP_BUILD;
  if (clientBuild) {
    headers["x-client-build"] = clientBuild;
  }

  // Add authentication token if required
  if (requiresAuth) {
    const token = await getAuthToken();
    if (!token) {
      throw new Error("Authentication required");
    }
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Merge custom headers if provided
  if (options?.headers) {
    Object.assign(headers, options.headers);
  }

  // Build URL with query parameters
  let url = `${API_BASE_URL}${endpoint}`;
  if (options?.params) {
    const searchParams = new URLSearchParams();
    Object.entries(options.params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, String(value));
      }
    });
    if (searchParams.toString()) {
      url += `?${searchParams.toString()}`;
    }
  }

  // Prepare request options
  const requestOptions: RequestInit = {
    method,
    headers,
    body: isFormData ? data : (data ? JSON.stringify(data) : undefined),
    // Avoid browser revalidation and conditional requests that can return 304s with empty bodies
    cache: "no-store",
  };
  // Make the request
  const response = await fetch(url, requestOptions);

  // If upstream returned 304 with no body, treat it as empty success to avoid parse errors
  if (response.status === 304) {
    return undefined as T;
  }

  // Parse the response. Handle cases where response might not be JSON or is empty.
  let responseData: unknown = {};
  try {
    responseData = await response.json();
  } catch (error) {
    // If parsing fails (e.g. empty response for 204), and response is ok, this is not an error itself.
    if (!response.ok) {
      throw new Error(
        `HTTP error ${response.status} - Failed to parse JSON response ${error}`
      );
    }
    // For OK responses with no content (e.g., 204), responseData remains {}
  }

  // Handle API errors
  const isObject = (v: unknown): v is Record<string, unknown> =>
    typeof v === "object" && v !== null;

  if (!response.ok) {
    let errorMessage = `API request failed with status ${response.status}`;
    let errorCode: string | undefined;

    if (isObject(responseData)) {
      // Try to extract error message
      const maybeError = responseData["error"];
      if (typeof maybeError === "string") {
        errorMessage = maybeError;
      } else if (isObject(maybeError) && typeof maybeError["message"] === "string") {
        errorMessage = String(maybeError["message"]);
      } else if (typeof (responseData as Record<string, unknown>)["message"] === "string") {
        errorMessage = String((responseData as Record<string, unknown>)["message"]);
      }

      // Try to extract error code
      if (typeof (responseData as Record<string, unknown>)["code"] === "string") {
        errorCode = String((responseData as Record<string, unknown>)["code"]);
      }
    }

    throw new ApiError(errorMessage, errorCode, response.status);
  }

  // Handle successful response
  if (isObject(responseData) && "data" in responseData) {
    return (responseData as Record<string, unknown>)["data"] as T;
  }
  // If no 'data' wrapper, return the whole response if it's not empty and response was OK
  if (isObject(responseData) && Object.keys(responseData).length > 0) {
    return responseData as T;
  }
  // For 204 No Content or empty JSON response on success, allow returning undefined if T can be undefined
  return undefined as T;
};
